<template>
  <Modal
    v-model:visible="showModal"
    :closable="false"
    :modal="true"
    :draggable="false"
    :dismissable-mask="false"
    append-to="self"
  >
    <template #header>
      <span v-if="modalTitle" class="p-dialog-title">
        {{ modalTitle }}
      </span>
      <div class="p-dialog-header-icons">
        <DSButton
          v-if="!stateMachine.matches('exitFlow')"
          aria-label="close"
          icon="pi pi-times"
          class="p-dialog-header-icon p-dialog-header-close p-link"
          @click="attemptAbandonFlow"
        />
      </div>
    </template>

    <div :class="$style['bnpl-wrapper']">
      <div v-if="showSubheading">
        <div :class="$style['clarify-wrapper']">
          <div :class="$style['bnpl-intro']" v-if="isInvoiceFlow">
            {{
              $t('account.pages.pageVendors.bnplPaymentSchedule.invoiceIntro')
            }}
          </div>
          <div :class="$style['bnpl-intro']" v-else>
            <i18n-t
              keypath="account.pages.pageVendors.bnplPaymentSchedule.cashAdvanceIntro"
              tag="span"
            >
              <template #link>
                <a :class="$style.link" @click="prev">
                  <u>
                    {{
                      $t(
                        'account.pages.pageVendors.bnplPaymentSchedule.cashAdvanceIntroLinkText',
                      )
                    }}
                  </u>
                </a>
              </template>
            </i18n-t>
          </div>
          <template v-if="showSelfReported">
            <CapacityOverview
              title="Funding Capacity"
              title-tag="Self-Reported"
              :available-capacity="remainingSelfReportedCapacityCents"
              :used-capacity="
                selfReportedCapacityCents - remainingSelfReportedCapacityCents
              "
              :total-capacity="selfReportedCapacityCents"
              :capacity-calculating="capacityCalculating"
              :disclaimer="DISCLAIMERS.VERIFYING_SALES_ACCOUNTS"
              condensed-version
            />
          </template>
          <template v-else-if="showPreliminaryCapacity">
            <CapacityOverview
              title="Funding Capacity"
              title-tag="Preliminary"
              :available-capacity="businessCapacity?.availableCents"
              :used-capacity="businessCapacity?.usedCents"
              :total-capacity="businessCapacity?.totalCents"
              :disclaimer="DISCLAIMERS.PRELIMINARY_CAPACITY"
              condensed-version
            />
          </template>
          <template v-else>
            <CapacityOverview
              :title="`${isApprovedRollingCapacity ? 'Rolling Funding' : 'Funding'} Capacity`"
              title-tag="Approved"
              :available-capacity="businessCapacity?.availableCents"
              :used-capacity="businessCapacity?.usedCents"
              :total-capacity="businessCapacity?.totalCents"
              :capacity-calculating="capacityCalculating"
              :disclaimer="DISCLAIMERS.APPROVED_CAPACITY"
              condensed-version
            />
          </template>
        </div>
      </div>

      <div :class="$style.container" size="large">
        <div v-if="isBnplWaitlisted" :class="$style['bnpl-waitlist']">
          <p>
            {{ $t('account.pages.pageVendors.bnplWaitlistedModalContent') }}
          </p>
          <DSButton
            :label="$t('account.pages.pageVendors.bnplWaitlistedModalClose')"
            class="p-button-secondary"
            @click="done"
          />
        </div>
        <template v-else>
          <ChooseFundingRequestType
            v-if="stateMachine.matches('flow.chooseFundingRequestType')"
            :direct-user-to-bill-modal="directUserToBillModal"
            @amount="updateAmount"
            @next-tab="next"
          />
          <UploadInvoice
            v-if="stateMachine.matches('flow.uploadInvoice')"
            :file-uploaded="fileUploaded"
            :requirements-checked="payload.userAcknowledgedRequirements"
            @next-tab="next"
            @prev-tab="prev"
            @updateFileUploaded="updateFileUploaded"
            @updateFile="uploadedFileParameters"
            @updateCheckbox="updateCheckbox"
            @getUploadedFile="prefillBillDetails"
            @removeFile="removeFile"
          />
          <!-- Invoice Details -->
          <InvoiceDetailsForm
            v-if="stateMachine.matches('flow.invoiceDetailsForm')"
            :due-date="payload.dueDate"
            :beneficiary-id="payload.beneficiaryId"
            :amount-cents="payload.amountCents"
            :selected-currency="payload.payOutCurrency"
            :memo="payload.memo"
            @next-tab="next"
            @prev-tab="prev"
            @dueDate="updateDueDate"
            @beneficiaryId="updateBeneficiaryId"
            @update-memo="updateMemo"
            @update-repayment-schedule="updateRepaymentSchedule"
          />
          <PaymentBeneficiaryDetails
            v-if="stateMachine.matches('flow.beneficiaryDetails')"
            :bill="payload"
            :processed-data="fileProcessedData"
            is-invoice-upload-step
            @vendor-created="handleVendorCreation"
            @beneficiaryId="updateBeneficiaryId"
            @next-tab="next"
            @prev-tab="prev"
          />
          <BnplPaymentSchedule
            v-if="stateMachine.matches('flow.bnplPaymentSchedule')"
            :amount="`${amount}`"
            :selected-term="repaymentTerm"
            :payload-currency="payload.payOutCurrency"
            :payload-due-date="payload.dueDate"
            @repayment-details="updateRepaymentDetails"
            @prev-tab="prev"
            @next-tab="next"
            @get-repayment-term="getRepaymentTerm"
            @amount="updateAmount"
            @pay-out-currency="updatePayOutCurrency"
          />
          <BnplPaymentScheduleConfirmation
            v-if="stateMachine.matches('flow.bnplPaymentScheduleConfirmation')"
            :amount-cents="payload.amountCents"
            :upload-url="this.currentUpload.downloadUrl"
            :upload-name="payload.uploadFilename"
            :repayment-details="repaymentDetails"
            :selected-currency="payload.payOutCurrency"
            :beneficiary-id="payload.beneficiaryId"
            :submitting-bill="submittingBill"
            :submission-error="billSubmissionError"
            @create-bill="createBill"
            @prev-tab="prev"
          />
          <PaymentAdded
            v-if="stateMachine.matches('flow.paymentAdded')"
            ref="paymentAddedModal"
            :payload="payload"
            @prev-tab="prev"
            @done="done"
            @addAnotherInvoice="addAnotherInvoice"
            @verifyRevenue="goToVerifyRevenuePage"
          />
          <ExitConfirmation
            v-if="stateMachine.matches('exitFlow')"
            @confirmExit="discardApplication"
            @giveUpExit="giveUpExit"
          />
        </template>
      </div>

      <div
        v-if="showCancellationBanner()"
        :class="$style['cancellation-banner']"
      >
        <Message severity="info" @close="closeCancellationBanner">
          <p>
            You can now cancel your submission up until the contract signing
            stage.
            <a
              href="https://help.clear.co/s/article/How-to-submit-an-invoice"
              target="_blank"
            >
              Learn More</a
            >
          </p>
        </Message>
      </div>
    </div>
  </Modal>
</template>

<script>
import { mapGetters } from 'vuex';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { dollarsToCents, formatMoney } from '@/utils/currency';
import { validateChildren } from '@/composables/validation';
import { mapRequestStatuses } from '@/utils/vuex-api-utils';
import {
  BillPaymentMethods,
  BNPL_TYPES,
  PAY_OUT_CURRENCY_OPTIONS,
  DEFAULT_CURRENCY,
  V2_CAPACITY_ERROR_TYPES,
} from '@/data/payments';
import { RouteNames } from '@/data/routes';
import { DISCLAIMERS } from '@/data/capacity';

import { useMachine } from '@xstate/vue';
import { getPaymentStateMachine } from '@/stateMachines/payments';
import { getShortBillId } from '@/utils/payments';
import { LocalStorage, LocalStorageKeys } from '@/utils/local-storage';

import Modal from '@/components/shared/Dialog.vue';
import DSButton from 'primevue/button';
import Message from 'primevue/message';
import CapacityOverview from '@/components/CapacityOverview.vue';
import PaymentBeneficiaryDetails from '@/pages/payments/components/PaymentBeneficiaryDetails.vue';

import UploadInvoice from './UploadInvoice.vue';
import InvoiceDetailsForm from './InvoiceDetailsForm.vue';
import PaymentAdded from './PaymentAdded.vue';
import BnplPaymentSchedule from './BnplPaymentSchedule.vue';
import ChooseFundingRequestType from './ChooseFundingRequestType.vue';
import ExitConfirmation from './ExitConfirmation.vue';
import BnplPaymentScheduleConfirmation from './BnplPaymentScheduleConfirmation.vue';
import { BillType } from '@/@types/bill.types';

const CAPACITY_ALERT_LEVEL_IN_CENTS = 100000;

const initialState = () => {
  return {
    fileUploaded: false,
    showModal: false,
    amount: '',
    repaymentTerm: 1,
    repaymentDetails: {
      termRate: 0,
      selectedTerm: 0,
      paymentPerTerm: 0,
      feeCents: 0,
      feeVersionId: 0,
    },
    payload: {
      paymentMethod: BillPaymentMethods.BNPL,
      payOutCurrency: PAY_OUT_CURRENCY_OPTIONS.USD.code,
      memo: '',
      userAcknowledgedRequirements: false,
    },
    newBeneficiaryCreated: false,
    submittingBill: false,
    billSubmissionError: null,
    fileProcessedData: {},
    DISCLAIMERS,
  };
};

const confirmationExitStates = [
  'uploadInvoice',
  'paymentMethods',
  'bnplPaymentSchedule',
  'invoiceDetailsForm',
  'bnplPaymentScheduleConfirmation',
  'beneficiaryDetails',
];

export default {
  components: {
    PaymentBeneficiaryDetails,
    BnplPaymentSchedule,
    ChooseFundingRequestType,
    BnplPaymentScheduleConfirmation,
    UploadInvoice,
    InvoiceDetailsForm,
    PaymentAdded,
    ExitConfirmation,
    Modal,
    DSButton,
    Message,
    CapacityOverview,
  },
  props: {
    directUserToBillModal: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const { state: stateMachine, send } = useMachine(getPaymentStateMachine());
    const { hasError } = validateChildren();
    return { stateMachine, send, hasError };
  },
  data() {
    return initialState();
  },
  computed: {
    ...mapGetters([
      'business',
      'currentUpload',
      'createdBill',
      'repaymentSchedule',
      'hasActiveSalesAccounts',
      'isBnplWaitlisted',
      'billFlowType',
      'isInvoiceFlow',
      'beneficiaries',
      'businessCapacity',
      'isProcessDocumentOcrEnabled',
      'selfReportedCapacityCents',
      'remainingSelfReportedCapacityCents',
      'businessCapacityGenerated',
      'isApprovedRollingCapacity',
    ]),
    ...mapRequestStatuses({
      createPayInvoiceRequest: 'CREATE_PAY_INVOICE',
      businessCapacityRequest: 'GET_BUSINESS_CAPACITY',
    }),
    commonAnalyticsProperties() {
      return {
        businessId: this.business.id,
        businessName: this.business.name,
        billType: this.billFlowType,
      };
    },
    determineType() {
      if (this.isInvoiceFlow) {
        return {
          invoiceDetailsFormType: this.$t('account.pages.pageVendors.invoice'),
          uploadInvoiceType: this.$t('account.pages.pageVendors.invoice'),
        };
      } else {
        return {
          invoiceDetailsFormType: '',
          uploadInvoiceType: '',
        };
      }
    },
    modalTitle() {
      if (this.isBnplWaitlisted) {
        return this.$t('account.pages.pageVendors.bnplWaitlistedModalTitle');
      } else if (this.stateMachine.matches('exitFlow')) {
        return 'Not ready to free up your cash flow?';
      }

      switch (this.stateMachine.value.flow) {
        case 'chooseFundingRequestType':
          return this.$t(
            'account.pages.pageVendors.chooseFundingRequestType.title',
          );
        case 'bnplPaymentSchedule':
          return this.$t('account.pages.pageVendors.bnplPaymentSchedule.title');
        case 'invoiceDetailsForm':
          return this.$t('account.pages.pageVendors.invoiceDetailsForm.title', {
            type: this.determineType.invoiceDetailsFormType,
          });
        case 'uploadInvoice':
          return this.$t('account.pages.pageVendors.uploadInvoiceModal.title', {
            type: this.determineType.uploadInvoiceType,
          });
        case 'bnplPaymentScheduleConfirmation':
          return this.$t(
            'account.pages.pageVendors.bnplPaymentScheduleConfirmation.title',
          );
        case 'beneficiaryDetails':
          return this.$t('paymentsPage.vendorDetails');
        default:
          return '';
      }
    },
    showSubheading() {
      return this.stateMachine.value.flow === 'bnplPaymentSchedule';
    },
    showSelfReported() {
      return !this.businessCapacityGenerated;
    },
    showPreliminaryCapacity() {
      return (
        this.businessCapacityGenerated && !this.businessCapacity.approvedAt
      );
    },
    capacityCalculating() {
      const errorType = this.businessCapacityRequest.error?.type;

      return (
        errorType === V2_CAPACITY_ERROR_TYPES.CAPACITY_NOT_FOUND &&
        !this.businessCapacity?.totalCents &&
        this.hasActiveSalesAccounts
      );
    },
  },
  methods: {
    resetScrollPosition() {
      const modalContent = document.querySelector('.p-dialog-content');
      if (modalContent) {
        modalContent.scrollTop = 0;
        modalContent.scrollLeft = 0;
      }
    },
    formatMoneyAndCurrency(amountCents, currencyType) {
      return `${formatMoney(amountCents, currencyType)} ${currencyType}`;
    },
    getRepaymentTerm(val) {
      this.repaymentTerm = Number(val);
    },
    giveUpExit() {
      this.send('GIVE_UP');
    },
    uploadedFileParameters() {
      this.payload.uploadFilename = this.currentUpload.name;
      this.payload.uploadId = this.currentUpload.id;
    },
    updateCheckbox(value) {
      this.payload.userAcknowledgedRequirements = value;
    },
    /** @public */
    async open() {
      this.resetUploadSteps();
      this.showModal = true;
    },
    async attemptAbandonFlow() {
      if (confirmationExitStates.includes(this.stateMachine.value.flow)) {
        analytics.track(SEGMENT_EVENTS.if_start_abandon_bill_submission, {
          ...this.commonAnalyticsProperties,
        });
        this.send('EXIT');
        return;
      }
      if (
        this.stateMachine.matches('flow.paymentAdded') &&
        this.$refs.paymentAddedModal.showConnectRevenueAccountModal
      ) {
        analytics.track(SEGMENT_EVENTS.if_connect_revenue_account_dismissed, {
          ...this.commonAnalyticsProperties,
          modalContext: 'bill_submission_flow',
        });
      }
      await this.closeModal();
    },
    async discardApplication() {
      analytics.track(SEGMENT_EVENTS.if_abandon_bill_submission, {
        ...this.commonAnalyticsProperties,
        billType: this.billFlowType,
      });
      this.$store.dispatchApiAction('DELETE_USER_UPLOAD', {
        id: this.currentUpload.id,
      });
      await this.closeModal();
    },
    async done() {
      if (this.isBnplWaitlisted) {
        this.showModal = false;
        this.$emit('done');
        return;
      }
      await this.closeModal();
    },
    async closeModal() {
      this.$store.commit('RESET_BENEFICIARY_FORM_FIELD');
      this.resetUploadSteps();
      this.send('RESET');
      this.showModal = false;
      this.$emit('done');
      await this.$store.dispatch('UPDATE_BILL_FLOW_TYPE', BillType.INVOICE);
    },
    async addAnotherInvoice() {
      this.goBackToChooseFundingRequestType();
      await this.refreshBills();
    },
    goToVerifyRevenuePage() {
      this.showModal = false;
      this.$router.push({ name: RouteNames.CONNECTED_ACCOUNTS.INDEX });
    },
    async next(skip) {
      if (!skip) {
        const error = await this.hasError();
        if (error) {
          return;
        }
      }

      if (this.stateMachine.matches('flow.beneficiaryDetails')) {
        this.payload.beneficiarySkipped = skip;
      }

      this.send('NEXT', {
        billFlowType: this.billFlowType,
        isNewBeneficiary: this.payload.beneficiaryId === '+newvendor',
      });
    },
    goBackToChooseFundingRequestType() {
      this.resetUploadSteps();
      this.send('RESET');
      this.showModal = true;
    },
    prev() {
      this.send('PREVIOUS', {
        billFlowType: this.billFlowType,
      });
    },
    prefillBillDetails(file) {
      const { processedDocumentEntities } = file;
      if (
        this.isProcessDocumentOcrEnabled &&
        Object.keys(processedDocumentEntities ?? {}).length
      ) {
        this.fileProcessedData = processedDocumentEntities;
        if (processedDocumentEntities?.totalAmount) {
          const totalAmount = parseFloat(processedDocumentEntities.totalAmount);
          if (Number.isFinite(totalAmount)) {
            this.updateAmount(totalAmount);
          }
        }
        this.payload.dueDate = processedDocumentEntities?.dueDate;

        // Invoice allows those listed in PAY_OUT_CURRENCY_OPTIONS
        if (
          this.isInvoiceFlow &&
          PAY_OUT_CURRENCY_OPTIONS[processedDocumentEntities?.currency ?? '']
        ) {
          this.payload.payOutCurrency = processedDocumentEntities?.currency;
        }
      }
    },
    updateFileUploaded(fileId) {
      if (fileId) {
        this.fileUploaded = true;
      }
    },
    removeFile() {
      this.fileUploaded = false;
    },
    updateAmount(amount) {
      this.amount = amount || 0;
      this.payload.amountCents = dollarsToCents(amount);
    },
    updatePayOutCurrency(currency) {
      this.payload.payOutCurrency = currency;
    },
    updateDueDate(dueDate) {
      this.payload.dueDate = dueDate;
    },
    updateRepaymentDetails(repaymentDetails) {
      this.repaymentDetails = repaymentDetails;
      this.payload.bnplDetails = {
        repaymentTerm: repaymentDetails.selectedTerm,
        feeVersionId: repaymentDetails.feeVersionId,
      };
      this.startRepaymentTerm = repaymentDetails.selectedTerm;
      if (repaymentDetails.dueDate) {
        this.payload.dueDate = repaymentDetails.dueDate;
      }
    },
    updateRepaymentSchedule() {
      this.repaymentDetails.repaymentSchedule =
        this.repaymentSchedule?.repaymentSchedulesByMonth[
          this.repaymentDetails.selectedTerm
        ].schedule;
      this.repaymentDetails.exchangeRateData =
        this.repaymentSchedule.exchangeRateData;
    },
    updateBeneficiaryId(beneficiaryId) {
      this.payload.beneficiaryId = beneficiaryId;
    },
    async resetUploadSteps() {
      Object.assign(this.$data, initialState());
      this.$store.dispatch('RESET_SELECTED_BILL');
    },
    async createBill() {
      this.submittingBill = true;
      this.payload.businessId = this.business.id;
      this.payload.bnplDetails.bnplType = BNPL_TYPES.CAPPED_IRR_MCA;

      // Send along draft conversion data for non-default currency payouts
      if (this.payload.payOutCurrency !== DEFAULT_CURRENCY) {
        this.payload.payOutFxRate =
          this.repaymentDetails.exchangeRateData.conversionRate;
        this.payload.payOutConvertedAmountCents =
          this.repaymentDetails.exchangeRateData.convertedAmountCents;
      }
      this.payload.type = this.billFlowType;

      // send beneficiary id only if it is valid
      let payload, vendorName;
      if (this.isInvoiceFlow) {
        vendorName = this.beneficiaries.find(
          (item) => item.id === this.payload.beneficiaryId,
        )?.name;
      }
      const { beneficiaryId, ...restOfPayload } = this.payload;
      if (beneficiaryId === '+newvendor') {
        payload = restOfPayload;
      } else {
        // if deprecate v0 beneficiaries is enabled the beneficiary record is actually a v2 record, therefore
        // we can drop replace the `beneficiaryId` key with `beneficiaryV2Id` and keep the same value
        if (this.isDeprecateV0BeneficiariesEnabled) {
          this.payload.beneficiaryV2Id = this.payload.beneficiaryId;
          this.payload.beneficiaryId = null;
        }

        payload = this.payload;
      }

      await this.$store.dispatchApiAction('CREATE_PAY_INVOICE', payload);

      if (this.createPayInvoiceRequest.isSuccess) {
        if (this.createdBill) {
          const analyticsProperties = {
            ...this.commonAnalyticsProperties,
            billId: this.createdBill.id,
            shortBillId: getShortBillId(this.createdBill.id),
            selectedTerm: this.repaymentDetails.numWeeklyPayments,
            billAmount: this.formatMoneyAndCurrency(
              this.createdBill.amountCents,
              this.createdBill.payOutCurrency,
            ),
            cappedAmount: this.formatMoneyAndCurrency(
              100 * this.repaymentDetails.paymentPerTerm,
              this.createdBill.payOutCurrency,
            ),
            dueDate: this.createdBill.dueDate,
            uploadType: this.createdBill.type,
            hasPaymentDetails:
              (!!beneficiaryId && beneficiaryId !== '+newvendor') ||
              this.newBeneficiaryCreated,
            vendorName,
          };
          analytics.track(
            SEGMENT_EVENTS.if_bill_submitted,
            analyticsProperties,
          );
        }

        await this.refreshBills();

        if (
          this.businessCapacity.availableCents < CAPACITY_ALERT_LEVEL_IN_CENTS
        ) {
          analytics.track('if_user_reach_full_capacity', {
            capacity: this.businessCapacity.availableCents,
          });
        }
        this.next();
        this.submittingBill = false;
      } else {
        this.billSubmissionError = this.createPayInvoiceRequest.error;
      }
    },
    async refreshBills() {
      await this.$store.dispatchApiAction('GET_BILLS');
      await this.$store.dispatchApiAction('GET_BUSINESS_CAPACITY');
    },
    updateMemo(value) {
      this.payload.memo = value;
    },
    handleVendorCreation() {
      this.newBeneficiaryCreated = true;
    },
    showCancellationBanner() {
      return (
        this.stateMachine.value.flow === 'chooseFundingRequestType' &&
        LocalStorage.getItem(LocalStorageKeys.CANCELLATION_BANNER) !== true
      );
    },
    closeCancellationBanner() {
      LocalStorage.setItem(LocalStorageKeys.CANCELLATION_BANNER, true);
    },
  },
  mounted() {
    this.$store.commit('RESET_BENEFICIARY_FORM_FIELD');
  },
  watch: {
    stateMachine(newState) {
      this.resetScrollPosition();
      let eventName;

      if (newState.matches('flow.uploadInvoice')) {
        eventName = SEGMENT_EVENTS.VIEWED_INVOICE_MODAL_ADD_INVOICE;
      } else if (newState.matches('flow.bnplPaymentSchedule')) {
        eventName = SEGMENT_EVENTS.VIEWED_INVOICE_MODAL_CHOOSE_PLAN;
      } else if (newState.matches('flow.bnplPaymentScheduleConfirmation')) {
        eventName = SEGMENT_EVENTS.VIEWED_INVOICE_MODAL_PLAN_DETAILS;
      } else if (newState.matches('flow.beneficiaryDetails')) {
        eventName = SEGMENT_EVENTS.VIEWED_INVOICE_MODAL_VENDOR_DETAILS;
      } else if (newState.matches('flow.invoiceDetailsForm')) {
        // TODO (SC-192257): Update segment event name
        eventName = SEGMENT_EVENTS.VIEWED_BILL_DETAILS_FORM;
      }

      if (eventName) {
        analytics.track(eventName, this.commonAnalyticsProperties);
      }
    },
  },
};
</script>
<style lang="less" module>
.container {
  margin: 0 auto;
  min-height: unset;

  h2,
  h3,
  h4 {
    font-family: @regular-font;
  }

  @media only screen and (max-width: 500px) {
    text-align: left;
  }
}

.clarify-wrapper {
  margin: 20px 0 40px;
}

.subheading {
  text-align: center;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 14px;
}

.subheading__item + .subheading__item {
  border-top: 1px solid rgba(0, 0, 0, 0.2);
  padding-top: 6px;
  margin-top: 6px;
}

.subheading__item span {
  color: #7f30ff;
}

.bnpl-waitlist {
  font-family: @regular-font;

  p {
    color: @color-black;
    font-weight: 300;
    font-size: 1rem;
    line-height: 24px;
    margin: 0 0 30px;
  }
}

.cancellation-banner {
  margin: 20px 0 0;
  max-width: 500px;
}

.bnpl-intro {
  font-size: 16px;
  margin-bottom: 32px;
}

.bnpl-wrapper {
  max-width: 700px;
  margin: 0 auto;
  padding: 16px;
}

.link {
  color: #5b00f0;
  text-decoration: underline;
  cursor: pointer;
}
</style>
